/* eslint-disable operator-linebreak */
import Keycloak from "keycloak-js"

export type Role = 'ROLE_ENHANCER' | 'ROLE_BUYER' | 'ROLE_LANGUAGE_MINER' 
    | 'ROLE_POLISHER' | 'ROLE_TRUST_MINER' | 'ROLE_PLAIN' 
    | 'ROLE_REDACTOR' | 'ROLE_BI' | 'ROLE_EXFLUENCY_ADMIN' 
    | 'ROLE_COMMUNITY_MANAGER' | 'ROLE_PLATFORM_MANAGER' 
    | 'ROLE_SUPPORT' | 'ROLE_OCR_ENGINE' 
    | 'ROLE_ANONYMIZATION_ENGINE' | 'ROLE_MT_ENGINE' | 'ROLE_GATED_COMMUNITY_MANAGER' | 'ROLE_GC_PROJECT_MANAGER' | 'ROLE_KNOWLEDGE_MINER'
    | 'ROLE_DATA_SOURCE_ADMIN'
export interface TokenUserInfo {
  id: string,
  login: string,
  name: string,
  surname: string,
  email: string,
  currency: string,
  timezone: string
}

export enum UserRole {
  PLAIN = "ROLE_PLAIN",
  ENHANCER = "ROLE_ENHANCER",
  TRUST_MINER = "ROLE_TRUST_MINER",
  REDACTOR = "ROLE_REDACTOR",
  BUYER = "ROLE_BUYER",
  EXFLUENCY_ADMIN = "ROLE_EXFLUENCY_ADMIN",
  PLATFORM_MANAGER = "ROLE_PLATFORM_MANAGER",
  GATED_COMMUNITY_MANAGER = "ROLE_GATED_COMMUNITY_MANAGER",
  GC_PROJECT_MANAGER = "ROLE_GC_PROJECT_MANAGER",
  KNOWLEDGE_MINER = "ROLE_KNOWLEDGE_MINER",
  DATA_SOURCE_ADMIN = "ROLE_DATA_SOURCE_ADMIN",
  SUPPORT = "ROLE_SUPPORT",
  LANGUAGE_MINER = "ROLE_LANGUAGE_MINER"
}

export const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.PLAIN]: "Plain",
  [UserRole.ENHANCER]: "Enhancer",
  [UserRole.TRUST_MINER]: "Trust Miner",
  [UserRole.REDACTOR]: "Redactor",
  [UserRole.BUYER]: "Requester",
  [UserRole.EXFLUENCY_ADMIN]: "Exfluency Admin",
  [UserRole.PLATFORM_MANAGER]: "Platform Manager",
  [UserRole.GATED_COMMUNITY_MANAGER]: "Gated Community Manager",
  [UserRole.GC_PROJECT_MANAGER]: "GC Project Manager",
  [UserRole.KNOWLEDGE_MINER]: "Knowledge Miner",
  [UserRole.DATA_SOURCE_ADMIN]: "Data Source Admin",
  [UserRole.LANGUAGE_MINER]: "Language Miner",
  [UserRole.SUPPORT]: "Support"
}

export function getRoleLabel(role: Role): string {
  return ROLE_LABELS[role as UserRole] ?? role
}

const _kc = Keycloak({
  url: process.env.REACT_APP_AUTHORIZATION_SERVER_URL ? process.env.REACT_APP_AUTHORIZATION_SERVER_URL : window.location.origin + "/auth",
  clientId: "exfluency-portal-frontend",
  realm: "exfluency-realm"
})

const MARKEN_SSO_COOKIE = 'exfluency_marken_sso'

const isMarkenSsoEntry = () =>
  window.location.pathname.split('/').includes('marken')

const markenSsoCookieSuffix = (): string => {
  let suffix = 'Path=/; Max-Age=2147483647; SameSite=Lax'
  const host = window.location.hostname
  if (host.endsWith('.exfluency.com')) {
    suffix += '; Domain=.exfluency.com'
  }
  if (window.location.protocol === 'https:') {
    suffix += '; Secure'
  }
  return suffix
}

const rememberMarkenSsoEntry = () => {
  document.cookie = `${MARKEN_SSO_COOKIE}=1; ${markenSsoCookieSuffix()}`
}

/** Drop /marken from the current URL so Keycloak redirect_uri is not that path. Keeps origin, other path parts, query, hash. */
const stripMarkenPathFromLocation = () => {
  const url = new URL(window.location.href)
  const segments = url.pathname.split('/').filter(segment => segment.length > 0 && segment !== 'marken')
  url.pathname = '/' + segments.join('/')
  const next = `${url.pathname}${url.search}${url.hash}`
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (next !== current) {
    window.history.replaceState(window.history.state, '', next)
  }
}

const initKeycloak = (onAuthenticatedCallback: () => void) => {
  if (isMarkenSsoEntry()) {
    rememberMarkenSsoEntry()
    stripMarkenPathFromLocation()
  }
  _kc.init({
    onLoad: 'check-sso',
    pkceMethod: 'S256'
  })
    .then((authenticated) => {
      if (!authenticated && !unauthenticatedPath()) {
        doLogin()
      } else {
        onAuthenticatedCallback()
      }
    })
    .catch(console.error)
}

const unauthenticatedPath = () => ['/logout', '/terms-of-service', '/privacy-notice'].some(e => e === window.location.pathname)
  || window.location.pathname.includes('/chat-bot')

const doLogin = _kc.login

const doLogout = _kc.logout

const isLoggedIn = () => !!_kc.token

const getToken = async () => {
  await updateToken(() => (console))
  return _kc.token
}

const updateToken = (successCallback: () => void) =>
  _kc.updateToken(10)
    .then(successCallback)
    .catch(doLogin)

const getUsername = () => _kc.tokenParsed?.preferred_username

const getTokenUserInfo = (): TokenUserInfo => {
  const token = _kc.tokenParsed as any
  return {
    id: token?.exfluencyId || '',
    login: token?.preferred_username || '',
    name: token?.given_name || '',
    surname: token?.family_name || '',
    email: token?.email || '',
    currency: token?.exfluencyCurrency || '',
    timezone: token?.exfluencyTimezone || 'Europe/Warsaw'
  }}

const hasRoles = (checkRoles: Role[]): boolean => {
  return checkRoles.some(role => _kc.hasRealmRole(role))
}

const isAuthenticated = () => _kc.authenticated &&
  _kc.token ? Promise.resolve() : Promise.reject("Failed to obtain access token.")

const UserService = {
  initKeycloak,
  doLogin,
  doLogout,
  isLoggedIn,
  getToken,
  getTokenUserInfo,
  updateToken,
  hasRoles,
  unauthenticatedPath,
  isAuthenticated
}

export default UserService
